import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { TWclassNames } from "./Div";

/**
 * BulkBar — floating bottom bar shown when rows are selected.
 *
 * actions: Array of:
 *   { label, icon: HeroiconComponent, action: fn, danger?: bool, disabled?: bool }
 *   { separator: true }  → renders a vertical divider
 *
 * Usage:
 *   <BulkBar
 *     count={selectedItems.length}
 *     onClear={() => setSelectedItems([])}
 *     actions={[
 *       { label: "Edit", icon: PencilIcon, action: openEditDialog },
 *       { separator: true },
 *       { label: "Delete", icon: TrashIcon, action: handleDelete, danger: true },
 *     ]}
 *   />
 */
export const BulkBar = ({ count, onClear, actions = [] }) => {
  const { t } = useTranslation();
  if (!count) return null;

  return (
    <>
      <style>{`@keyframes pm-bulkbar-in{from{opacity:0;transform:translate(-50%,14px)}}`}</style>
      <div
        style={{ animation: "pm-bulkbar-in 0.18s cubic-bezier(.2,.8,.2,1)" }}
        className="fixed bottom-6 left-1/2 z-[120] -translate-x-1/2 flex items-center gap-1 rounded-[14px] bg-slate-900 px-4 py-2 shadow-[0_16px_44px_rgba(0,0,0,0.20),0_4px_12px_rgba(0,0,0,0.10)]"
      >
        {/* Count badge */}
        <span className="flex items-center gap-2.5 pr-2 text-[14px] font-semibold text-white whitespace-nowrap">
          <span className="rounded-[7px] bg-blue-500 px-2.5 py-0.5 text-xs tabular-nums">
            {count}
          </span>
          {t("selected")}
        </span>

        {/* Actions */}
        {actions.map((action, i) => {
          if (action.separator) {
            return <div key={i} className="mx-1 h-6 w-px bg-white/15" />;
          }
          return (
            <button
              key={i}
              type="button"
              onClick={action.action}
              disabled={action.disabled}
              title={action.title}
              className={TWclassNames(
                "inline-flex h-[34px] items-center gap-1.5 rounded-[9px] px-3 text-[13.5px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40",
                action.danger
                  ? "text-slate-300 hover:bg-red-600 hover:text-white"
                  : "text-slate-300 hover:bg-white/10 hover:text-white"
              )}
            >
              {action.icon && <action.icon className="h-[15px] w-[15px]" />}
              {action.label}
            </button>
          );
        })}

        {/* Separator before clear */}
        <div className="mx-1 h-6 w-px bg-white/15" />

        {/* Clear button */}
        <button
          type="button"
          onClick={onClear}
          title={t("Clear selection")}
          className="grid h-[34px] w-[34px] place-items-center rounded-[9px] text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
        >
          <XMarkIcon className="h-[17px] w-[17px]" />
        </button>
      </div>
    </>
  );
};
